import { useState, useEffect, useCallback } from 'react';
import { useProvince } from '../../hooks/useProvince';
import { COLORS } from '../../constants/theme';
import { searchSnapshots, getMyIntel, type SnapshotSearchResponse, type ProvinceSnapshotDto } from '../../api/intel';
import { FilterBlock } from './FilterBlock';
import { MultiSelectDropdown } from '../ui/MultiSelectDropdown';
import { CollapsibleSection } from './CollapsibleSection';
import { RangeControl } from './RangeControl';
import { IntelExpandedRow } from './IntelExpandedRow';
import { IntelButtons } from './IntelButtons';
import { ProtectedBadge } from '../ProtectedBadge';
import { isProtected } from '../../utils/protection';
import { buildTargetSearchParams, type SortKey } from '../../utils/targetSearchParams';
import { parseIntelData } from '../../types/intel';
import type { CrystalBallData, SpyOnMilitaryData, SurveyData } from '../../types/intel';
import { TARGET_FINDER_RACES as RACES, TARGET_FINDER_RANKS as RANKS, TARGET_FINDER_PRESETS as PRESETS } from '../../constants/targetFinderOptions';

interface TargetFinderTabProps {
  worldId: string;
  provinceId: string;
  results: SnapshotSearchResponse | null;
  onResults: (results: SnapshotSearchResponse | null) => void;
  expandedId: string | null;
  onExpandedId: (id: string | null) => void;
}

export function TargetFinderTab({
  worldId, provinceId, results, onResults, expandedId, onExpandedId,
}: TargetFinderTabProps) {
  // Filter states
  const [raceEnabled, setRaceEnabled] = useState(false);
  const [raceSelected, setRaceSelected] = useState<string[]>([]);
  const [rankEnabled, setRankEnabled] = useState(false);
  const [rankSelected, setRankSelected] = useState<string[]>([]);

  const { province } = useProvince();
  const ownNetworth = province?.metadata.networth ?? null;

  const [nwEnabled, setNwEnabled] = useState(true);
  const [nwMin, setNwMin] = useState('80');
  const [nwMax, setNwMax] = useState('120');
  const [nwPreset, setNwPreset] = useState(0);

  const [landEnabled, setLandEnabled] = useState(false);
  const [landMin, setLandMin] = useState('80');
  const [landMax, setLandMax] = useState('120');
  const [landPreset, setLandPreset] = useState(0);

  const [nwpaEnabled, setNwpaEnabled] = useState(false);
  const [nwpaMin, setNwpaMin] = useState('80');
  const [nwpaMax, setNwpaMax] = useState('120');
  const [nwpaPreset, setNwpaPreset] = useState(0);

  const [kdNwEnabled, setKdNwEnabled] = useState(false);
  const [kdNwMin, setKdNwMin] = useState('80');
  const [kdNwMax, setKdNwMax] = useState('120');
  const [kdNwPreset, setKdNwPreset] = useState(0);

  const [kdLandEnabled, setKdLandEnabled] = useState(false);
  const [kdLandMin, setKdLandMin] = useState('80');
  const [kdLandMax, setKdLandMax] = useState('120');
  const [kdLandPreset, setKdLandPreset] = useState(0);

  // Section collapse state
  const [provinceFiltersOpen, setProvinceFiltersOpen] = useState(true);
  const [kingdomFiltersOpen, setKingdomFiltersOpen] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState<SortKey>('nw');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const playerStats = results?.player ?? null;

  // Preset handler factory
  function handlePreset(
    setPreset: (i: number) => void,
    setLower: (v: string) => void,
    setUpper: (v: string) => void,
  ) {
    return (index: number) => {
      setPreset(index);
      const p = PRESETS[index];
      if (p.lower != null) {
        setLower(String(p.lower));
        setUpper(String(p.upper!));
      }
    };
  }

  // Auto-select Custom preset when user manually edits input
  function makeLowerHandler(setVal: (v: string) => void, setPreset: (i: number) => void) {
    return (v: string) => { setVal(v); setPreset(3); };
  }
  function makeUpperHandler(setVal: (v: string) => void, setPreset: (i: number) => void) {
    return (v: string) => { setVal(v); setPreset(3); };
  }

  const handleSearch = useCallback(async (searchPage = 0) => {
    setLoading(true);
    setError(null);
    setPage(searchPage);

    try {
      const params = buildTargetSearchParams(
        {
          raceEnabled, raceSelected, rankEnabled, rankSelected,
          nwEnabled, nwMin, nwMax, landEnabled, landMin, landMax,
          nwpaEnabled, nwpaMin, nwpaMax, kdNwEnabled, kdNwMin, kdNwMax,
          kdLandEnabled, kdLandMin, kdLandMax,
        },
        sortBy,
        sortDir,
        searchPage,
        50,
        playerStats,
        ownNetworth
      );

      const response = await searchSnapshots(worldId, params);
      onResults(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [worldId, sortBy, sortDir, raceEnabled, raceSelected, rankEnabled, rankSelected,
      nwEnabled, nwMin, nwMax, landEnabled, landMin, landMax,
      nwpaEnabled, nwpaMin, nwpaMax, kdNwEnabled, kdNwMin, kdNwMax,
      kdLandEnabled, kdLandMin, kdLandMax, onResults, playerStats, ownNetworth]);

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  }

  function toggleExpand(id: string) {
    onExpandedId(expandedId === id ? null : id);
  }

  function handleRowHover(e: React.MouseEvent<HTMLTableRowElement>, enter: boolean, isExpanded: boolean, defaultBg: string) {
    if (!isExpanded) {
      (e.currentTarget as HTMLTableRowElement).style.background = enter ? COLORS.sectionHeader : defaultBg;
    }
  }

  // Active filter counts
  const provinceFilterCount = [raceEnabled, rankEnabled, nwEnabled, nwpaEnabled, landEnabled].filter(Boolean).length;
  const kingdomFilterCount = [kdNwEnabled, kdLandEnabled].filter(Boolean).length;
  const totalFilterCount = provinceFilterCount + kingdomFilterCount;

  return (
    <div>
      {/* Filter panel */}
      <div
        style={{
          backgroundColor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: 14,
        }}
      >
        {/* Province Filters */}
        <CollapsibleSection
          title="Province Filters"
          accent={COLORS.link}
          collapsed={!provinceFiltersOpen}
          onToggle={() => setProvinceFiltersOpen(o => !o)}
          rightContent={provinceFilterCount > 0 ? `${provinceFilterCount} active` : undefined}
        >
          <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'start' }}>
            <FilterBlock label="Race" enabled={raceEnabled} onToggle={setRaceEnabled}>
              <MultiSelectDropdown options={RACES} selected={raceSelected} onChange={setRaceSelected} />
            </FilterBlock>
            <FilterBlock label="Rank (Honour)" enabled={rankEnabled} onToggle={setRankEnabled}>
              <MultiSelectDropdown options={RANKS} selected={rankSelected} onChange={setRankSelected} />
            </FilterBlock>
            <FilterBlock label="Province Networth" enabled={nwEnabled} onToggle={setNwEnabled}>
              <RangeControl
                refValue={playerStats?.networth ?? ownNetworth}
                refLabel="gc"
                presets={PRESETS}
                activePreset={nwPreset}
                onPresetSelect={handlePreset(setNwPreset, setNwMin, setNwMax)}
                lowerVal={nwMin}
                upperVal={nwMax}
                onLower={makeLowerHandler(setNwMin, setNwPreset)}
                onUpper={makeUpperHandler(setNwMax, setNwPreset)}
                unit="%"
              />
            </FilterBlock>
            <FilterBlock label="NW/Acre" enabled={nwpaEnabled} onToggle={setNwpaEnabled}>
              <RangeControl
                refValue={playerStats?.nwpa ?? null}
                presets={PRESETS}
                activePreset={nwpaPreset}
                onPresetSelect={handlePreset(setNwpaPreset, setNwpaMin, setNwpaMax)}
                lowerVal={nwpaMin}
                upperVal={nwpaMax}
                onLower={makeLowerHandler(setNwpaMin, setNwpaPreset)}
                onUpper={makeUpperHandler(setNwpaMax, setNwpaPreset)}
                unit="%"
              />
            </FilterBlock>
            <FilterBlock label="Province Land" enabled={landEnabled} onToggle={setLandEnabled}>
              <RangeControl
                refValue={playerStats?.land ?? null}
                refLabel="acres"
                presets={PRESETS}
                activePreset={landPreset}
                onPresetSelect={handlePreset(setLandPreset, setLandMin, setLandMax)}
                lowerVal={landMin}
                upperVal={landMax}
                onLower={makeLowerHandler(setLandMin, setLandPreset)}
                onUpper={makeUpperHandler(setLandMax, setLandPreset)}
                unit="%"
              />
            </FilterBlock>
            <div />
          </div>
        </CollapsibleSection>

        {/* Kingdom Filters */}
        <CollapsibleSection
          title="Kingdom Filters"
          accent={COLORS.gold}
          collapsed={!kingdomFiltersOpen}
          onToggle={() => setKingdomFiltersOpen(o => !o)}
          rightContent={kingdomFilterCount > 0 ? `${kingdomFilterCount} active` : undefined}
        >
          <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'start' }}>
            <FilterBlock label="Kingdom Networth" enabled={kdNwEnabled} onToggle={setKdNwEnabled}>
              <RangeControl
                refValue={playerStats?.kingdomNW ?? null}
                refLabel="gc"
                presets={PRESETS}
                activePreset={kdNwPreset}
                onPresetSelect={handlePreset(setKdNwPreset, setKdNwMin, setKdNwMax)}
                lowerVal={kdNwMin}
                upperVal={kdNwMax}
                onLower={makeLowerHandler(setKdNwMin, setKdNwPreset)}
                onUpper={makeUpperHandler(setKdNwMax, setKdNwPreset)}
                unit="%"
              />
            </FilterBlock>
            <FilterBlock label="Kingdom Land" enabled={kdLandEnabled} onToggle={setKdLandEnabled}>
              <RangeControl
                refValue={playerStats?.kingdomLand ?? null}
                refLabel="acres"
                presets={PRESETS}
                activePreset={kdLandPreset}
                onPresetSelect={handlePreset(setKdLandPreset, setKdLandMin, setKdLandMax)}
                lowerVal={kdLandMin}
                upperVal={kdLandMax}
                onLower={makeLowerHandler(setKdLandMin, setKdLandPreset)}
                onUpper={makeUpperHandler(setKdLandMax, setKdLandPreset)}
                unit="%"
              />
            </FilterBlock>
            <div />
          </div>
        </CollapsibleSection>

        {/* Footer: filter count + search button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderTop: `1px solid ${COLORS.border}`,
            background: COLORS.sectionHeader,
          }}
        >
          <span style={{ color: COLORS.text.muted, fontSize: 11 }}>
            {totalFilterCount === 0
              ? 'No filters active'
              : `${totalFilterCount} filter${totalFilterCount !== 1 ? 's' : ''} active`}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {error && <span style={{ color: COLORS.negative, fontSize: 12 }}>{error}</span>}
            <button
              onClick={() => handleSearch(0)}
              disabled={loading}
              style={{
                padding: '6px 24px',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer',
                backgroundColor: COLORS.btnPrimary,
                color: '#e8ecf4',
                border: 'none',
                opacity: loading ? 0.7 : 1,
              }}
              onMouseEnter={e => { if (!loading) (e.target as HTMLButtonElement).style.backgroundColor = COLORS.btnPrimaryHover; }}
              onMouseLeave={e => { (e.target as HTMLButtonElement).style.backgroundColor = COLORS.btnPrimary; }}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </div>

      {/* Results table */}
      {results && (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <CollapsibleSection
            title="Results"
            accent={COLORS.link}
            collapsed={false}
            onToggle={() => {}}
            rightContent={`${results.totalCount.toLocaleString()} found`}
          />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100, color: COLORS.text.primary }}>
              <thead>
                <tr>
                  <SortHeader label="Province" sortKey={undefined} align="left" width="15%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Race" sortKey="race" align="left" width="6%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Land" sortKey="land" align="right" width="6%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Networth" sortKey="nw" align="right" width="9%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="NW/A" sortKey="nwpa" align="right" width="6%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Rel." sortKey={undefined} align="right" width="5%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Kingdom" sortKey={undefined} align="left" width="15%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Kd Land" sortKey="kdLand" align="right" width="6%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Kd NW" sortKey="kdNw" align="right" width="9%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <SortHeader label="Kd NW/A" sortKey={undefined} align="right" width="6%" current={sortBy} dir={sortDir} onClick={handleSort} />
                  <th style={{ ...thStyle, textAlign: 'center', width: '7%' }}>Intel</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map((row, idx) => {
                  const isExpanded = expandedId === row.provinceId;
                  const isEven = idx % 2 === 0;
                  const defaultBg = isExpanded ? COLORS.sectionHeader : isEven ? 'transparent' : COLORS.tableRowAlt;
                  const bb = isExpanded ? 'none' : `1px solid ${COLORS.border}`;
                  const nwPct = pctCalc(row.networth, playerStats?.networth ?? 0);
                  const kdNwpa = row.kingdomLand > 0 ? Math.round(row.kingdomNw / row.kingdomLand) : 0;

                  return (
                    <ResultRow
                      key={row.provinceId}
                      row={row}
                      defaultBg={defaultBg}
                      bb={bb}
                      nwPct={nwPct}
                      kdNwpa={kdNwpa}
                      isExpanded={isExpanded}
                      onToggle={() => toggleExpand(row.provinceId)}
                      onExpand={() => onExpandedId(row.provinceId)}
                      onHover={(e, enter) => handleRowHover(e, enter, isExpanded, defaultBg)}
                      worldId={worldId}
                      provinceId={provinceId}
                      currentTick={results.currentTick}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 14px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', background: COLORS.sectionHeader }}>
            <span style={{ color: COLORS.text.muted, fontSize: 10 }}>Click row to expand · Intel saved to Intel Hub</span>
            <span style={{ color: COLORS.text.muted, fontSize: 10 }}>CB = mana · SM/SV = stealth</span>
          </div>

          {/* Pagination */}
          {results.totalCount > results.size && (
            <div style={{ display: 'flex', gap: '8px', padding: '8px 14px', justifyContent: 'center', borderTop: `1px solid ${COLORS.border}` }}>
              <button
                onClick={() => handleSearch(page - 1)}
                disabled={page === 0 || loading}
                style={{ ...paginationStyle, opacity: page === 0 ? 0.4 : 1 }}
              >
                Previous
              </button>
              <span style={{ color: COLORS.text.secondary, fontSize: '12px', alignSelf: 'center' }}>
                Page {page + 1} of {Math.ceil(results.totalCount / results.size)}
              </span>
              <button
                onClick={() => handleSearch(page + 1)}
                disabled={(page + 1) * results.size >= results.totalCount || loading}
                style={{ ...paginationStyle, opacity: (page + 1) * results.size >= results.totalCount ? 0.4 : 1 }}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Helpers ---

function pctCalc(value: number, ref: number): number {
  if (!ref) return 0;
  return Math.round((value / ref) * 100);
}

function pctColor(pct: number): string {
  if (pct < 70) return COLORS.positive;
  if (pct < 90) return '#82c8a0';
  if (pct <= 110) return COLORS.text.secondary;
  if (pct <= 130) return '#d4975f';
  return '#d48060';
}

function titleCase(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

// --- Sub-components ---

const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const paginationStyle: React.CSSProperties = {
  backgroundColor: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '4px',
  color: COLORS.text.secondary,
  padding: '4px 12px',
  fontSize: '12px',
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  padding: '8px 6px',
  color: COLORS.text.tertiary,
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  background: COLORS.cardAlt,
  borderBottom: `1px solid ${COLORS.border}`,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

function SortHeader({ label, sortKey, align, width, current, dir, onClick }: {
  label: string;
  sortKey: SortKey | undefined;
  align?: 'left' | 'right' | 'center';
  width?: string;
  current?: SortKey;
  dir?: 'asc' | 'desc';
  onClick?: (key: SortKey) => void;
}) {
  const isActive = sortKey && current === sortKey;

  return (
    <th
      style={{ ...thStyle, textAlign: align ?? 'left', width }}
      onClick={() => sortKey && onClick?.(sortKey)}
    >
      {label}
      {sortKey ? (
        isActive
          ? <span style={{ color: COLORS.link, marginLeft: 3, fontSize: 9 }}>{dir === 'asc' ? '▲' : '▼'}</span>
          : <span style={{ color: COLORS.text.muted, opacity: 0.3, marginLeft: 3, fontSize: 9 }}>⇅</span>
      ) : null}
    </th>
  );
}

function ResultRow({ row, defaultBg, bb, nwPct, kdNwpa, isExpanded, onToggle, onExpand, onHover, worldId, provinceId, currentTick }: {
  row: ProvinceSnapshotDto;
  defaultBg: string;
  bb: string;
  nwPct: number;
  kdNwpa: number;
  isExpanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onHover: (e: React.MouseEvent<HTMLTableRowElement>, enter: boolean) => void;
  worldId: string;
  provinceId: string;
  currentTick: number;
}) {
  const protectedTarget = isProtected(row.protectionExpiresAtTick, currentTick);
  const [intelData, setIntelData] = useState<{
    cb?: CrystalBallData | null;
    som?: SpyOnMilitaryData | null;
    sv?: SurveyData | null;
  }>({});
  const [fetchCount, setFetchCount] = useState(0);

  const fetchIntel = useCallback(async () => {
    try {
      const response = await getMyIntel(worldId, { target: row.provinceId, size: 50 });
      const cb = response.reports.find(r => r.type === 'CB');
      const som = response.reports.find(r => r.type === 'SOM');
      const sv = response.reports.find(r => r.type === 'SURVEY');
      setIntelData({
        cb: cb ? (parseIntelData(cb).data as CrystalBallData) : null,
        som: som ? (parseIntelData(som).data as SpyOnMilitaryData) : null,
        sv: sv ? (parseIntelData(sv).data as SurveyData) : null,
      });
    } catch {
      // Silently fail — expanded row will show "No data"
    }
  }, [worldId, row.provinceId]);

  useEffect(() => {
    if (isExpanded) {
      fetchIntel();
    }
  }, [isExpanded, fetchIntel, fetchCount]);

  const handleIntelGathered = useCallback(() => {
    setFetchCount(c => c + 1);
    if (!isExpanded) {
      onExpand();
    }
  }, [isExpanded, onExpand]);

  const mono: React.CSSProperties = { fontFamily: MONO, fontSize: 11 };
  const cell: React.CSSProperties = { padding: '7px 6px', borderBottom: bb };

  return (
    <>
      <tr
        style={{ background: defaultBg, cursor: 'pointer' }}
        onClick={onToggle}
        onMouseEnter={e => onHover(e, true)}
        onMouseLeave={e => onHover(e, false)}
      >
        {/* Province */}
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
          <span style={{ fontWeight: 500 }}>{row.provinceName}</span>{' '}
          <span style={{ color: COLORS.link, ...mono }}>({row.location})</span>
          {protectedTarget && (
            <span style={{ marginLeft: 6 }}>
              <ProtectedBadge protectionExpiresAtTick={row.protectionExpiresAtTick} currentTick={currentTick} />
            </span>
          )}
        </td>
        {/* Race */}
        <td style={{ ...cell, color: COLORS.text.secondary }}>{titleCase(row.race)}</td>
        {/* Land */}
        <td style={{ ...cell, textAlign: 'right', ...mono }}>
          {row.land.toLocaleString()}
        </td>
        {/* Networth */}
        <td style={{ ...cell, textAlign: 'right', ...mono }}>
          <span style={{ color: COLORS.gold }}>{row.networth.toLocaleString()}</span>
        </td>
        {/* NW/A */}
        <td style={{ ...cell, textAlign: 'right', ...mono, color: COLORS.text.secondary }}>
          {row.nwpa.toLocaleString()}
        </td>
        {/* Rel. (province NW) */}
        <td style={{ ...cell, textAlign: 'right', ...mono }}>
          <span style={{ color: pctColor(nwPct), fontWeight: 600 }}>{nwPct}%</span>
        </td>
        {/* Kingdom */}
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>
          <span style={{ fontWeight: 500 }}>{row.kingdomName}</span>{' '}
          <span style={{ color: COLORS.link, ...mono }}>({row.kingdomId})</span>
        </td>
        {/* Kd Land */}
        <td style={{ ...cell, textAlign: 'right', ...mono }}>
          {row.kingdomLand.toLocaleString()}
        </td>
        {/* Kd NW */}
        <td style={{ ...cell, textAlign: 'right', ...mono }}>
          <span style={{ color: COLORS.gold }}>{row.kingdomNw.toLocaleString()}</span>
        </td>
        {/* Kd NW/A */}
        <td style={{ ...cell, textAlign: 'right', ...mono, color: COLORS.text.secondary }}>
          {kdNwpa.toLocaleString()}
        </td>
        {/* Intel */}
        <td style={{ ...cell, padding: '7px 4px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
          <IntelButtons
            targetProvinceId={row.provinceId}
            worldId={worldId}
            playerProvinceId={provinceId}
            onIntelGathered={handleIntelGathered}
            thieveryDisabled={protectedTarget}
          />
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={11} style={{ padding: 0 }}>
            <IntelExpandedRow
              provinceId={row.provinceId}
              provinceName={row.provinceName}
              location={row.location}
              worldId={worldId}
              playerProvinceId={provinceId}
              cbData={intelData.cb}
              somData={intelData.som}
              svData={intelData.sv}
              onIntelGathered={handleIntelGathered}
              thieveryDisabled={protectedTarget}
            />
          </td>
        </tr>
      )}
    </>
  );
}
